import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { FLOW_LABELS, precaptureFlowEmails } from "@/lib/email-render";
import {
  ArrowLeft,
  Mail,
  Repeat,
  Zap,
  Flag,
  Pencil,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Send,
  Loader2,
  Check,
  Sparkles,
  Pencil as PencilIcon,
} from "lucide-react";

type FlowEmail = {
  id: string;
  name: string;
  status: "draft" | "sent_to_klaviyo";
  subjectLine?: string | null;
  personaNameSnapshot?: string | null;
  flowSequence?: number | null;
  // Background copy-generation progress (progressive load). null on legacy
  // flow emails created before progressive load (treated as fully ready).
  // "awaiting_template" = deferred stub waiting for the user to pick this
  // email's template (Classic or a preset) before anything generates.
  copyStatus?: "awaiting_template" | "pending" | "ready" | "failed" | null;
};

type PresetTemplateMeta = {
  key: string;
  name: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  // Rollout gate: disabled templates show as "coming soon" and can't be picked.
  enabled?: boolean;
};

type FlowDetail = {
  id: string;
  name: string;
  flowType: string;
  status: "draft" | "sending" | "sent_to_klaviyo" | "send_failed";
  createdAt?: string | null;
  emails: FlowEmail[];
};

export default function EmailFlowPage() {
  const { brandId, flowId } = useParams<{ brandId: string; flowId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const backLink = `/brands/${brandId}/email-express`;

  const flowKey = [`/api/brands/${brandId}/email-express/flows`, flowId];

  const {
    data: flow,
    isLoading,
    isError,
    error,
  } = useQuery<FlowDetail>({
    queryKey: flowKey,
    enabled: !!brandId && !!flowId,
    refetchInterval: (query) =>
      query.state.data?.status === "sending" ||
      (query.state.data?.emails ?? []).some(
        (e) => e.copyStatus === "pending",
      )
        ? 4000
        : false,
  });

  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [removeTarget, setRemoveTarget] = useState<FlowEmail | null>(null);
  // Per-stub template pick ("classic" or a preset key) before Generate is hit.
  const [templateChoice, setTemplateChoice] = useState<Record<string, string>>({});

  const hasAwaiting = (flow?.emails ?? []).some(
    (e) => e.copyStatus === "awaiting_template",
  );

  // Preset registry for the per-email template picker (only fetched while a
  // stub is still waiting on its pick).
  const { data: presetTemplates } = useQuery<PresetTemplateMeta[]>({
    queryKey: [`/api/brands/${brandId}/email-express/preset-templates`],
    enabled: !!brandId && hasAwaiting,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: flowKey });
    queryClient.invalidateQueries({
      queryKey: [`/api/brands/${brandId}/email-express/flows`],
    });
    queryClient.invalidateQueries({
      queryKey: [`/api/brands/${brandId}/email-express/emails`],
    });
    queryClient.invalidateQueries({
      queryKey: [`/api/brands/${brandId}/email-express/stats`],
    });
  };

  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest(
        "PATCH",
        `/api/brands/${brandId}/email-express/flows/${flowId}`,
        { name },
      );
      return (await res.json()) as FlowDetail;
    },
    onSuccess: () => {
      setRenaming(false);
      invalidate();
    },
    onError: (e: any) =>
      toast({ title: "Rename failed", description: e?.message, variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/flows/${flowId}/reorder`,
        { orderedIds },
      );
      return (await res.json()) as FlowDetail;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(flowKey, updated);
      invalidate();
    },
    onError: (e: any) =>
      toast({ title: "Reorder failed", description: e?.message, variant: "destructive" }),
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      // Raw fetch so a 422 voiceBriefError (the W1 Brand Voice Brief workflow
      // failed on a legacy flow with no persisted brief) surfaces its specific
      // message instead of apiRequest's generic "422: …" string.
      const res = await fetch(
        `/api/brands/${brandId}/email-express/flows/${flowId}/emails`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
          credentials: "include",
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const err = new Error(data?.message || `Couldn't add email (${res.status})`);
        if (data?.voiceBriefError) (err as any).voiceBriefError = true;
        throw err;
      }
      return (await res.json()) as FlowEmail & {
        heroCopyWarning?: string | null;
        sellingPointsCopyWarning?: string | null;
      };
    },
    onSuccess: (result) => {
      invalidate();
      toast({
        title: "Email added",
        description:
          "A new email slot was added at the end — choose its template to generate it.",
      });
      // Non-blocking: email was added with templated copy, but an AI copy
      // workflow (hero and/or selling points) reported missing inputs — surface
      // it so the user can fix + regen that block.
      if (result?.heroCopyWarning) {
        toast({
          title: "AI hero copy skipped",
          description: result.heroCopyWarning,
          variant: "default",
        });
      }
      if (result?.sellingPointsCopyWarning) {
        toast({
          title: "AI selling points skipped",
          description: result.sellingPointsCopyWarning,
          variant: "default",
        });
      }
    },
    onError: (e: any) =>
      toast({ title: "Couldn't add email", description: e?.message, variant: "destructive" }),
  });

  const selectTemplateMutation = useMutation({
    mutationFn: async ({
      emailId,
      presetTemplateKey,
    }: {
      emailId: string;
      presetTemplateKey: string | null;
    }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${emailId}/select-template`,
        { presetTemplateKey },
      );
      return (await res.json()) as FlowEmail;
    },
    onSuccess: () => {
      invalidate();
      toast({
        title: "Generating email",
        description: "This email is being written — it'll fill in right here.",
      });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't set the template",
        description: e?.message,
        variant: "destructive",
      }),
  });

  const removeMutation = useMutation({
    mutationFn: async (emailId: string) => {
      const res = await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/email-express/flows/${flowId}/emails/${emailId}`,
      );
      return (await res.json()) as FlowDetail;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(flowKey, updated);
      invalidate();
      setRemoveTarget(null);
    },
    onError: (e: any) => {
      toast({ title: "Couldn't remove email", description: e?.message, variant: "destructive" });
      setRemoveTarget(null);
    },
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      // Pre-capture every member email's preview sections BEFORE claiming the
      // send, so the server worker builds each email from the exact editor
      // preview (polished emails skip capture and send their polished stack).
      await precaptureFlowEmails({
        brandId: brandId!,
        emailIds: (flow?.emails ?? []).map((e) => e.id),
      });
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/flows/${flowId}/send`,
        {},
      );
      return (await res.json()) as FlowDetail & { klaviyo?: { message?: string } };
    },
    onSuccess: (updated) => {
      invalidate();
      toast({
        title: "Sending flow to Klaviyo",
        description:
          updated.klaviyo?.message ||
          "Building your flow and sending it to Klaviyo — you'll get a notification when it's ready.",
      });
    },
    onError: (e: any) =>
      toast({ title: "Send failed", description: e?.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="p-8 space-y-6" data-testid="loading-flow">
        <Skeleton className="h-10 w-72" />
        <div className="max-w-2xl mx-auto space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !flow) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-4 mt-16" data-testid="status-flow-error">
        <Repeat className="h-10 w-10 text-muted-foreground mx-auto" />
        <h1 className="text-xl font-bold">Couldn't load this flow</h1>
        <p className="text-sm text-muted-foreground">
          {(error as Error)?.message || "It may have been deleted or doesn't belong to this brand."}
        </p>
        <Button variant="outline" className="gap-2" onClick={() => navigate(backLink)} data-testid="link-back-error">
          <ArrowLeft className="h-4 w-4" /> Back to Email Express
        </Button>
      </div>
    );
  }

  const emails = [...flow.emails].sort(
    (a, b) => (a.flowSequence ?? 0) - (b.flowSequence ?? 0),
  );
  const isSent = flow.status === "sent_to_klaviyo";
  const isSending = flow.status === "sending";
  const tag = FLOW_LABELS[flow.flowType] || "Flow";
  const busy =
    reorderMutation.isPending || removeMutation.isPending || addMutation.isPending;

  const move = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= emails.length) return;
    const ids = emails.map((e) => e.id);
    [ids[index], ids[next]] = [ids[next], ids[index]];
    reorderMutation.mutate(ids);
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="page-email-flow">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 -ml-2 mb-2 text-muted-foreground"
            onClick={() => navigate(backLink)}
            data-testid="link-back"
          >
            <ArrowLeft className="h-4 w-4" /> Email Express
          </Button>
          {renaming ? (
            <div className="flex items-center gap-2">
              <Input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className="h-9 text-lg font-bold w-72"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && nameDraft.trim()) renameMutation.mutate(nameDraft.trim());
                  if (e.key === "Escape") setRenaming(false);
                }}
                data-testid="input-flow-name"
              />
              <Button
                size="sm"
                disabled={!nameDraft.trim() || renameMutation.isPending}
                onClick={() => renameMutation.mutate(nameDraft.trim())}
                data-testid="button-save-name"
              >
                {renameMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold truncate" data-testid="text-flow-name">
                {flow.name}
              </h1>
              {!isSent && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground"
                  onClick={() => {
                    setNameDraft(flow.name);
                    setRenaming(true);
                  }}
                  data-testid="button-rename-flow"
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <Badge variant="secondary" className="text-[11px] gap-1 font-normal">
              <Repeat className="h-3 w-3" /> {tag}
            </Badge>
            <Badge variant="outline" className="text-[11px] gap-1 font-normal text-muted-foreground">
              <Mail className="h-3 w-3" /> {emails.length} email{emails.length === 1 ? "" : "s"}
            </Badge>
            {isSent ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-500" data-testid="status-flow">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Sent to Klaviyo
              </span>
            ) : isSending ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-500" data-testid="status-flow">
                <Loader2 className="h-3 w-3 animate-spin" /> Sending…
              </span>
            ) : flow.status === "send_failed" ? (
              <Badge variant="outline" className="text-[11px] text-destructive border-destructive/40" data-testid="status-flow">
                Send failed
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[11px] text-muted-foreground" data-testid="status-flow">
                Draft
              </Badge>
            )}
          </div>
        </div>
        {!isSent && (
          <span
            className="shrink-0"
            title={
              hasAwaiting
                ? "Choose a template for every email before sending."
                : undefined
            }
          >
            <Button
              className="gap-2"
              disabled={isSending || sendMutation.isPending || hasAwaiting}
              onClick={() => sendMutation.mutate()}
              data-testid="button-send-flow"
            >
              {isSending || sendMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isSending || sendMutation.isPending ? "Sending…" : "Send to Klaviyo"}
            </Button>
          </span>
        )}
      </div>

      {/* Sequence */}
      <div className="relative">
        {/* Trigger */}
        <SequenceNode icon={<Zap className="h-4 w-4" />} tone="trigger">
          <div className="font-semibold text-sm">Trigger</div>
          <div className="text-xs text-muted-foreground">
            {tag} — set the timing &amp; entry rules in Klaviyo after sending.
          </div>
        </SequenceNode>

        <Connector />

        {emails.map((email, i) => (
          <div key={email.id}>
            <SequenceNode
              icon={<Mail className="h-4 w-4" />}
              tone="email"
              testId={`flow-email-${email.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-primary">
                      Email #{i + 1}
                    </span>
                    {email.status === "sent_to_klaviyo" && (
                      <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/40">
                        Sent
                      </Badge>
                    )}
                    {email.copyStatus === "awaiting_template" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] text-primary border-primary/40"
                        data-testid={`badge-awaiting-${email.id}`}
                      >
                        Pick a template
                      </Badge>
                    )}
                    {email.copyStatus === "pending" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] text-amber-600 border-amber-500/40 gap-1"
                        data-testid={`badge-generating-${email.id}`}
                      >
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        Generating
                      </Badge>
                    )}
                    {email.copyStatus === "failed" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] text-red-600 border-red-500/40"
                        data-testid={`badge-failed-${email.id}`}
                      >
                        Failed
                      </Badge>
                    )}
                  </div>
                  <div className="font-semibold text-sm truncate mt-0.5" data-testid={`text-email-name-${email.id}`}>
                    {email.name}
                  </div>
                  {email.copyStatus !== "awaiting_template" && email.subjectLine && (
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {email.subjectLine}
                    </div>
                  )}
                  {email.copyStatus === "awaiting_template" && !isSent && (
                    <div
                      className="mt-2.5 flex items-center gap-2 flex-wrap"
                      data-testid={`picker-template-${email.id}`}
                    >
                      <Select
                        value={templateChoice[email.id] ?? ""}
                        onValueChange={(v) =>
                          setTemplateChoice((prev) => ({ ...prev, [email.id]: v }))
                        }
                        disabled={selectTemplateMutation.isPending}
                      >
                        <SelectTrigger
                          className="h-8 w-64 text-xs"
                          data-testid={`select-template-${email.id}`}
                        >
                          <SelectValue placeholder="Choose a template…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="classic">
                            Classic — AI-designed layout
                          </SelectItem>
                          {/* Flow deferred selection deliberately offers only
                              `enabled` presets. bundle_pair (Perfect Pair) is
                              blockDriven + enabled:false: it is selectable in
                              the CAMPAIGN picker only — the flow rollout gate
                              (see shared/email-presets.ts) keeps it out of
                              flows, matching the pre-cleanup behavior where it
                              was a disabled "coming soon" stub here. */}
                          {(presetTemplates ?? [])
                            .filter((t) => t.enabled)
                            .map((t) => (
                              <SelectItem key={t.key} value={t.key}>
                                {t.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        className="h-8 gap-1.5"
                        disabled={
                          !templateChoice[email.id] ||
                          selectTemplateMutation.isPending
                        }
                        onClick={() =>
                          selectTemplateMutation.mutate({
                            emailId: email.id,
                            presetTemplateKey:
                              templateChoice[email.id] === "classic"
                                ? null
                                : templateChoice[email.id],
                          })
                        }
                        data-testid={`button-generate-${email.id}`}
                      >
                        {selectTemplateMutation.isPending &&
                        selectTemplateMutation.variables?.emailId === email.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                        Generate
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!isSent && (
                    <div className="flex flex-col">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={i === 0 || busy}
                        onClick={() => move(i, -1)}
                        data-testid={`button-up-${email.id}`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={i === emails.length - 1 || busy}
                        onClick={() => move(i, 1)}
                        data-testid={`button-down-${email.id}`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  {email.copyStatus !== "awaiting_template" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 h-7"
                      onClick={() => navigate(`/brands/${brandId}/email-express/${email.id}`)}
                      data-testid={`button-edit-${email.id}`}
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                  )}
                  {!isSent && emails.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      disabled={busy}
                      onClick={() => setRemoveTarget(email)}
                      data-testid={`button-remove-${email.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </SequenceNode>
            <Connector />
          </div>
        ))}

        {!isSent && (
          <>
            <div className="flex justify-center mb-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-dashed"
                disabled={addMutation.isPending}
                onClick={() => addMutation.mutate()}
                data-testid="button-add-email"
              >
                {addMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                Add email
              </Button>
            </div>
            <Connector />
          </>
        )}

        {/* End */}
        <SequenceNode icon={<Flag className="h-4 w-4" />} tone="end">
          <div className="font-semibold text-sm">End of flow</div>
        </SequenceNode>
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this email?</AlertDialogTitle>
            <AlertDialogDescription>
              “{removeTarget?.name}” will be deleted and the remaining emails will be re-numbered. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-remove">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => removeTarget && removeMutation.mutate(removeTarget.id)}
              data-testid="button-confirm-remove"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Connector() {
  return <div className="mx-auto w-px h-6 bg-border" aria-hidden />;
}

function SequenceNode({
  icon,
  tone,
  children,
  testId,
}: {
  icon: React.ReactNode;
  tone: "trigger" | "email" | "end";
  children: React.ReactNode;
  testId?: string;
}) {
  const toneClasses =
    tone === "trigger"
      ? "border-amber-500/40 bg-amber-500/5"
      : tone === "end"
        ? "border-border/60 bg-muted/30"
        : "border-border/60 bg-card";
  const iconClasses =
    tone === "trigger"
      ? "bg-amber-500/15 text-amber-600"
      : tone === "end"
        ? "bg-muted text-muted-foreground"
        : "bg-primary/10 text-primary";
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${toneClasses}`}
      data-testid={testId}
    >
      <div className={`shrink-0 grid place-items-center h-8 w-8 rounded-lg ${iconClasses}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
